import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import "../graphql/glPostingAccountOptions.query"

import { FULL_LIST_LIMIT } from "@/constants/common"
import {
  GetGlPostingAccountOptionsDocument,
  type GlAccount,
  type GlAccountQueryListArgs,
  type Query,
  ReportingAccountType,
} from "@/graphql/codegen/graphql"

export interface GlPostingAccountOption {
  id: string
  label: string
}

interface UseGlPostingAccountOptionsReturn {
  glPostingAccounts: GlPostingAccountOption[]
  isFetching: boolean
  error?: CombinedError
}

const transformToOption = (glAccount: GlAccount): GlPostingAccountOption => {
  const accountNumber = glAccount.subNumber ? `${glAccount.number}-${glAccount.subNumber}` : glAccount.number

  return {
    id: glAccount.glAccountId ?? "",
    label: `${accountNumber} - ${glAccount.title}`,
  }
}

export const useGlPostingAccountOptions = (): UseGlPostingAccountOptionsReturn => {
  const [glPostingAccounts, setGlPostingAccounts] = useState<GlPostingAccountOption[]>([])

  const [{ data, fetching, error }] = useQuery<Pick<Query, "glAccount">, GlAccountQueryListArgs>({
    query: GetGlPostingAccountOptionsDocument,
    variables: {
      input: {
        filter: {
          reportingAccountType: ReportingAccountType.Posting,
          isActive: true,
        },
        offset: 0,
        limit: FULL_LIST_LIMIT,
      },
    },
  })

  useEffect(() => {
    if (data?.glAccount.list.__typename !== "GlAccountListSuccess") return

    const accounts = data.glAccount.list.glAccounts
      .filter((glAccount): glAccount is GlAccount => glAccount !== null)
      .map(transformToOption)

    accounts.sort((a, b) => a.label.localeCompare(b.label))
    setGlPostingAccounts(accounts)
  }, [data])

  return {
    glPostingAccounts,
    isFetching: fetching,
    error,
  }
}
